import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { checklistQuestionAPI, checklistTemplateAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditCountryMaster.css";

const normalizeQuestion = (item) => ({
  questionCode: item.questionCode || item.QuestionCode || item.QuestionCODE || "",
  templateCode: item.templateCode || item.TemplateCode || item.TEMPLATECODE || "",
  questionTypeCode: item.questionTypeCode || item.QuestionTypeCode || item.QuestionTypeCODE || "",
  parentQuestionCode: item.parentQuestionCode || item.ParentQuestionCode || item.ParentQuestionCODE || null,
  questionText: item.questionText || item.QuestionText || item.QuestionText || "",
  questionAnsText: item.questionAnsText || item.QuestionAnsText || item.QuestionAnsText || "",
  displayOrder: item.displayOrder || item.DisplayOrder || item.DisplayOrder || 0,
  isMandatory: item.isMandatory ?? item.IsMandatory ?? true,
  isConditional: item.isConditional ?? item.IsConditional ?? false,
});

const AddEditChecklistQuestion = () => {
  const navigate = useNavigate();
  const { questionCode } = useParams();
  const [searchParams] = useSearchParams();
  const templateCode = searchParams.get("templateCode") || "";
  const isEditMode = !!questionCode;

  const [question, setQuestion] = useState({
    questionCode: "",
    questionTypeCode: "",
    parentQuestionCode: null,
    questionText: "",
    questionAnsText: "",
    displayOrder: 0,
    isMandatory: true,
    isConditional: false,
  });

  const [template, setTemplate] = useState(null);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [existingQuestions, setExistingQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!templateCode) {
      Swal.fire({
        icon: "warning",
        title: "Template Required",
        text: "Template code is required",
      }).then(() => {
        navigate("/checklist-templates");
      });
      return;
    }

    const initializeData = async () => {
      await Promise.all([
        loadTemplate(),
        loadQuestionTypes(),
        loadExistingQuestions(),
      ]);

      if (isEditMode) {
        await loadQuestion();
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionCode, templateCode]);

  const loadTemplate = async () => {
    try {
      const response = await checklistTemplateAPI.getByCode(templateCode);
      if (response?.success && response.data) {
        setTemplate(response.data);
      }
    } catch (e) {
      console.error("Error loading template:", e);
    }
  };

  const loadQuestionTypes = async () => {
    try {
      const response = await checklistQuestionAPI.getQuestionTypes();
      if (response?.success && response.data) {
        setQuestionTypes(response.data.map((qt) => ({
          code: qt.questionTypeCode || qt.QuestionTypeCode,
          name: qt.questionTypeName || qt.QuestionTypeName,
        })));
        // Set default question type if not in edit mode
        if (!isEditMode && response.data.length > 0) {
          setQuestion((prev) => ({
            ...prev,
            questionTypeCode: response.data[0].questionTypeCode || response.data[0].QuestionTypeCode,
          }));
        }
      } else {
        // Fallback to default types
        const defaultTypes = [
          { code: "QTYPE-001", name: "Text" },
          { code: "QTYPE-002", name: "Yes/No" },
          { code: "QTYPE-003", name: "Number" },
          { code: "QTYPE-004", name: "Date" },
          { code: "QTYPE-005", name: "Dropdown" },
          { code: "QTYPE-006", name: "Multiple Choice" },
        ];
        setQuestionTypes(defaultTypes);
        if (!isEditMode) {
          setQuestion((prev) => ({ ...prev, questionTypeCode: defaultTypes[0].code }));
        }
      }
    } catch (e) {
      console.error("Error loading question types:", e);
      const defaultTypes = [
        { code: "QTYPE-001", name: "Text" },
        { code: "QTYPE-002", name: "Yes/No" },
        { code: "QTYPE-003", name: "Number" },
        { code: "QTYPE-004", name: "Date" },
        { code: "QTYPE-005", name: "Dropdown" },
        { code: "QTYPE-006", name: "Multiple Choice" },
      ];
      setQuestionTypes(defaultTypes);
      if (!isEditMode) {
        setQuestion((prev) => ({ ...prev, questionTypeCode: defaultTypes[0].code }));
      }
    }
  };

  const loadExistingQuestions = async () => {
    try {
      const response = await checklistQuestionAPI.getByTemplate(templateCode, {
        pageNumber: 1,
        pageSize: 1000,
      });
      if (response?.success && response.data?.data) {
        const normalized = response.data.data.map(normalizeQuestion);
        setExistingQuestions(normalized);
        // Update display order for new questions
        if (!isEditMode) {
          const maxOrder = normalized.length > 0 
            ? Math.max(...normalized.map((q) => q.displayOrder || 0)) 
            : 0;
          setQuestion((prev) => ({ ...prev, displayOrder: maxOrder + 1 }));
        }
        return normalized;
      }
      return [];
    } catch (e) {
      console.error("Error loading existing questions:", e);
      return [];
    }
  };

  const loadQuestion = async () => {
    try {
      setLoading(true);
      const response = await checklistQuestionAPI.getByCode(questionCode);
      if (response && response.success && response.data) {
        setQuestion(normalizeQuestion(response.data));
      } else {
        navigate(`/checklist-questions/tree?templateCode=${templateCode}`);
      }
    } catch (e) {
      console.error("Error loading question:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load question details", "error");
      }
      navigate(`/checklist-questions/tree?templateCode=${templateCode}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setQuestion((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!question.questionText?.trim()) {
      Swal.fire("Error", "Question Text is required", "error");
      return false;
    }
    if (!question.questionTypeCode) {
      Swal.fire("Error", "Question Type is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const requestData = {
        ...question,
        templateCode: templateCode,
      };

      let response;
      if (isEditMode) {
        response = await checklistQuestionAPI.update(questionCode, requestData);
      } else {
        response = await checklistQuestionAPI.create(requestData);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || (isEditMode ? "Question updated successfully" : "Question created successfully"),
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate(`/checklist-questions/tree?templateCode=${templateCode}`);
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save question",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving question:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save question. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href={`/checklist-questions/tree?templateCode=${templateCode}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/checklist-questions/tree?templateCode=${templateCode}`);
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Checklist Questions
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Question" : "Add Question"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate(`/checklist-questions/tree?templateCode=${templateCode}`)}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Template Info */}
        {template && (
          <div className="card-panel mb-3">
            <Typography variant="h6" sx={{ mb: 2 }}>
              Template: <strong>{template.templateName || templateCode}</strong>
            </Typography>
            {template.templateDescription && (
              <Typography variant="body2" color="text.secondary">
                {template.templateDescription}
              </Typography>
            )}
          </div>
        )}

        {/* Question Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Question Text <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={question.questionText}
                  onChange={(e) => handleChange("questionText", e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Enter the question text"
                  required
                  className="add-edit-product-textfield"
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Question Type <span style={{ color: "red" }}>*</span>
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={question.questionTypeCode}
                    onChange={(e) => handleChange("questionTypeCode", e.target.value)}
                    className="add-edit-product-textfield"
                  >
                    {questionTypes.length === 0 ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : (
                      questionTypes.map((qt) => (
                        <MenuItem key={qt.code} value={qt.code}>
                          {qt.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Parent Question (Optional)
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={question.parentQuestionCode || ""}
                    onChange={(e) => handleChange("parentQuestionCode", e.target.value || null)}
                    className="add-edit-product-textfield"
                  >
                    <MenuItem value="">None (Root Question)</MenuItem>
                    {existingQuestions
                      .filter((q) => q.questionCode !== questionCode)
                      .map((q) => (
                        <MenuItem key={q.questionCode} value={q.questionCode}>
                          {q.questionText || q.questionCode}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <small className="text-muted">Select a parent question to create a hierarchical structure</small>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Default Answer / Options
                </label>
                <TextField
                  value={question.questionAnsText}
                  onChange={(e) => handleChange("questionAnsText", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter default answer or options (comma-separated for dropdown)"
                  className="add-edit-product-textfield"
                />
                <small className="text-muted">For dropdown/multiple choice, separate options with commas</small>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Display Order
                </label>
                <TextField
                  type="number"
                  value={question.displayOrder}
                  onChange={(e) => handleChange("displayOrder", parseInt(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  className="add-edit-product-textfield"
                />
              </div>
            </div>

            {!isEditMode && (
              <div className="col-12 col-md-6 col-lg-4">
                <div className="textfield-wrapper">
                  <label className="select2-label pnlLblsze">
                    Question Code
                  </label>
                  <TextField
                    value={question.questionCode}
                    onChange={(e) => handleChange("questionCode", e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Auto-generated if empty"
                    className="add-edit-product-textfield"
                  />
                </div>
              </div>
            )}

            <div className="col-12 col-md-6 col-lg-4">
              <div className="form-check form-switch d-flex align-items-center h-100">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isMandatorySwitch"
                  checked={question.isMandatory}
                  onChange={(e) => handleChange("isMandatory", e.target.checked)}
                />
                <label className="form-check-label ms-2" htmlFor="isMandatorySwitch">
                  Mandatory
                </label>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <div className="form-check form-switch d-flex align-items-center h-100">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isConditionalSwitch"
                  checked={question.isConditional}
                  onChange={(e) => handleChange("isConditional", e.target.checked)}
                />
                <label className="form-check-label ms-2" htmlFor="isConditionalSwitch">
                  Conditional
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate(`/checklist-questions/tree?templateCode=${templateCode}`)}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : isEditMode ? "Update" : "Create"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditChecklistQuestion;
